import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Auth0Provider, useAuth0, type AppState } from '@auth0/auth0-react';
import {
  AUTH0_AUDIENCE,
  AUTH0_CLIENT_ID,
  AUTH0_CONNECTION,
  AUTH0_DOMAIN,
  AUTH_MODE,
  CALLBACK_PATH,
  DEV_USER,
} from './env';
import { ApiError, installAuth } from './api/client';

/**
 * The dashboard's two-mode auth on the operator surface, minus everything this
 * app does not have: no react-query cache to clear, no user switcher, no
 * router. Dev mode is the default and mounts nothing at all — `api/client.ts`
 * already sends `x-dev-user` + `x-dev-admin: 1`, exactly as before.
 *
 * Auth0 mode mounts the provider and installs a bearer `authorize` into the
 * client. One tenant, one SPA client, one connection — the same three build
 * values the dashboard takes.
 */

export interface AdminAuth {
  /** The nav foot's fallback label before `GET /me` answers. */
  who: string;
  /** Auth0 only; there is no dev session to end. */
  signOut: (() => void) | null;
}

const AuthContext = createContext<AdminAuth>({ who: DEV_USER, signOut: null });

export function useAdminAuth(): AdminAuth {
  return useContext(AuthContext);
}

/**
 * **Where sign-in should land the reader back** — the path they were on when it
 * started, never the callback itself.
 *
 * It was always `/` while this app had no router and one page. It is not any
 * more: an org page is an address (`/organizations/:orgId/:tab`), so a deep link
 * followed by a fresh login has somewhere to come back to, and sending everybody
 * to the list would lose exactly the address that was worth sharing.
 *
 * `dashboard/src/auth/auth-context.tsx`'s `currentPath`, unchanged.
 */
function currentPath(): string {
  const { pathname, search } = window.location;
  if (pathname === CALLBACK_PATH) return '/';
  return `${pathname}${search}`;
}

/**
 * The provider does the code exchange wherever it lands; this only puts the
 * address bar back where the reader was, before the router ever reads it.
 *
 * `history.replaceState` rather than a router navigate on purpose: the router
 * mounts *below* this provider (main.tsx), so at the moment the exchange
 * finishes there is no navigate to call. The router then mounts on the restored
 * path, which is the same outcome with one fewer moving part.
 */
function onRedirectCallback(appState?: AppState): void {
  const target = typeof appState?.returnTo === 'string' ? appState.returnTo : '/';
  window.history.replaceState({}, document.title, target);
}

/**
 * The dashboard's two session flags, same keys, same reasoning
 * (`dashboard/src/auth/auth-context.tsx`):
 *
 *   `ni:relogin-attempted` — a 401 has already bounced this visit through
 *     Auth0 once. The server 401s valid sessions too (no `app_user` row yet,
 *     an unverified email, a `sub` collision — server/src/auth/
 *     user-verifier.ts), and against a live session `loginWithRedirect` comes
 *     straight back with the same identity: an infinite redirect.
 *
 *   `ni:signed-out` — the reader asked to leave. Logout returns to the origin,
 *     the app remounts, and the still-live Google session would let the
 *     auto-login effect sign them right back in.
 *
 * Every access is wrapped: a browser with site data blocked throws on the
 * getter, and the page has to render anyway.
 */
const RELOGIN_KEY = 'ni:relogin-attempted';
const SIGNED_OUT_KEY = 'ni:signed-out';

function flagIsSet(key: string): boolean {
  try {
    return window.sessionStorage.getItem(key) !== null;
  } catch {
    return false;
  }
}

function setFlag(key: string): void {
  try {
    window.sessionStorage.setItem(key, '1');
  } catch {
    // Storage refused. The in-page guards still hold for this load.
  }
}

function clearFlag(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // As above.
  }
}

function Auth0Session({ children }: { children: ReactNode }) {
  const {
    isLoading,
    isAuthenticated,
    error,
    user,
    getAccessTokenSilently,
    loginWithRedirect,
    logout,
  } = useAuth0();

  /** A live session the API will not have. Terminal, with one way out. */
  const [noAccess, setNoAccess] = useState(false);
  /** Auth0 would not mint a token even after a fresh trip through sign-in. */
  const [tokenError, setTokenError] = useState<string | null>(null);
  /** Written before the logout redirect; read back on the load after it. */
  const [signedOut, setSignedOut] = useState(() => flagIsSet(SIGNED_OUT_KEY));

  // One redirect per page load: StrictMode double-invokes effects, and several
  // reads can 401 together.
  const redirecting = useRef(false);
  /** On the way to Auth0: the pages below would render the synthetic 401. */
  const [leaving, setLeaving] = useState(false);

  const startLogin = useCallback(() => {
    if (redirecting.current) return;
    redirecting.current = true;
    setLeaving(true);
    loginWithRedirect({ appState: { returnTo: currentPath() } }).catch(() => {
      redirecting.current = false;
      setLeaving(false);
    });
  }, [loginWithRedirect]);

  /**
   * With a live session a 401 is not a sign-in problem — the server knows this
   * identity and will not have it, and another trip through Auth0 returns the
   * same person and the same 401. Skip the redirect and say so. Without a
   * session one attempt is worth making, and the flag holds that "one" across
   * the page load the attempt causes.
   */
  const handleUnauthorized = useCallback(() => {
    if (isAuthenticated || flagIsSet(RELOGIN_KEY)) {
      setNoAccess(true);
      return;
    }
    setFlag(RELOGIN_KEY);
    startLogin();
  }, [isAuthenticated, startLogin]);

  /**
   * A token that will not mint is a sign-in problem, never an account one: the
   * request never reached the server. Usually Auth0 has refused the stored
   * refresh token (a 403 `invalid_grant` from `/oauth/token`) while the SDK
   * still reports `isAuthenticated`. One trip through Auth0 mints a fresh pair;
   * a second failure is shown as what it is.
   */
  const handleTokenFailure = useCallback(
    (cause: unknown) => {
      if (!flagIsSet(RELOGIN_KEY)) {
        setFlag(RELOGIN_KEY);
        startLogin();
        return;
      }
      setTokenError(cause instanceof Error ? cause.message : 'The session could not be renewed.');
    },
    [startLogin],
  );

  useEffect(() => {
    // A reader who just signed out is not an unauthenticated reader to send
    // back to Google — the Google session is still live and would return them
    // instantly. They get a screen and a button instead.
    if (signedOut) return;
    if (isLoading || isAuthenticated || error) return;
    startLogin();
  }, [signedOut, isLoading, isAuthenticated, error, startLogin]);

  // Installed during render, not from an effect: the page's own load effects
  // fire before a parent effect would, and the first `GET /me` has to carry the
  // token. See installAuth's note.
  installAuth({
    authorize: async (headers) => {
      let token: string;
      try {
        token = await getAccessTokenSilently();
      } catch (cause) {
        handleTokenFailure(cause);
        throw new ApiError(401, 'HTTP_401', 'Not signed in.');
      }
      headers.set('authorization', `Bearer ${token}`);
    },
    onUnauthorized: handleUnauthorized,
    // Any non-401 answer proves the session reaches the server, which retires
    // the one-shot for the next visit.
    onAccepted: () => clearFlag(RELOGIN_KEY),
  });

  const signOut = useCallback(() => {
    // Set BEFORE the redirect: this is the only moment the app is still running
    // and knows the departure was deliberate.
    setFlag(SIGNED_OUT_KEY);
    void logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout]);

  /** The one explicit sign-in: both guards are retired by the reader asking. */
  const signInAgain = useCallback(() => {
    clearFlag(SIGNED_OUT_KEY);
    clearFlag(RELOGIN_KEY);
    setSignedOut(false);
    setNoAccess(false);
    setTokenError(null);
    redirecting.current = false;
    startLogin();
  }, [startLogin]);

  const value = useMemo<AdminAuth>(
    () => ({
      who: user?.name ?? user?.email ?? '',
      signOut,
    }),
    [user, signOut],
  );

  if (signedOut) {
    return (
      <div className="main">
        <div className="column">
          <div className="empty-line">You&rsquo;ve been signed out.</div>
          <button type="button" className="btn act tiny" onClick={signInAgain}>
            Sign in
          </button>
        </div>
      </div>
    );
  }

  if (error || tokenError) {
    return (
      <div className="main">
        <div className="column">
          <div className="flag row">
            <div className="label">Not signed in</div>
            <div className="detail">{error?.message ?? tokenError}</div>
          </div>
          <button type="button" className="btn act tiny" onClick={signInAgain}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (noAccess) {
    return (
      <div className="main">
        <div className="column">
          <div className="flag row">
            <div className="label">No account</div>
            <div className="detail">
              You&rsquo;re signed in as {user?.email ?? 'this Google account'}, but no account is
              provisioned for this identity yet.
            </div>
          </div>
          <button type="button" className="btn act tiny" onClick={signOut}>
            Sign out
          </button>
        </div>
      </div>
    );
  }

  if (leaving || isLoading || !isAuthenticated) {
    return (
      <div className="main">
        <div className="column">
          <div className="empty-line">Signing in.</div>
        </div>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  if (AUTH_MODE !== 'auth0') return <>{children}</>;

  return (
    <Auth0Provider
      domain={AUTH0_DOMAIN}
      clientId={AUTH0_CLIENT_ID}
      authorizationParams={{
        audience: AUTH0_AUDIENCE,
        // Required: Terraform registers `<origin>/callback` and nothing else,
        // so the bare origin would be a "Callback URL mismatch".
        redirect_uri: `${window.location.origin}${CALLBACK_PATH}`,
        connection: AUTH0_CONNECTION,
      }}
      useRefreshTokens
      cacheLocation="localstorage"
      onRedirectCallback={onRedirectCallback}
    >
      <Auth0Session>{children}</Auth0Session>
    </Auth0Provider>
  );
}
